/**
 * Customer details page
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer, commitLocalUpdate } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import {
  Avatar, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Hidden, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
  FormControlLabel, Switch, FormControl, InputLabel, Select, Divider,
  Tab, Tabs, TableContainer, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import IntegrationCountFetch from './IntegrationCountFetch';
import {
  Add, CloudQueue, Contacts, CreditCard, Edit, EvStation, Delete,
  HelpOutline, MonetizationOn, Wallet
} from '@mui/icons-material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilled from '@mui/icons-material/PlayCircleFilled';
import SubdirectoryArrowRight from '@mui/icons-material/SubdirectoryArrowRight';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import PercentageTextField from '../ui/PercentageTextField';
import AccountUpdateMutation from './AccountUpdateMutation';
import FundingSourceList from '../paymentsources/FundingSourceList';
import CustomerBudget from '../paymentsources/CustomerBudget';
import CustomerProjectWallet from '../paymentsources/CustomerProjectWallet'
import CustomerChargeLimit from '../paymentsources/CustomerChargeLimit';
import SavingSpinner from '../ui/SavingSpinner';

import Subscriptions from './Subscriptions';
import Services from './Services';
import SubscriptionOrders from '../customer/SubscriptionOrders';
import AccountLedgerSummaryFetch from './AccountLedgerSummaryFetch';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SaveButton from '../ui/SaveButton';
import ConfirmationDialog from '../ui/ConfirmationDialog';

//import PhoneNumberFormat from '../ui/PhoneNumberFormat';
import Address from '../ui/Address'
import CurrencyTextField from '../ui/CurrencyTextField';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import CustomerFinance from './CustomerFinance';
import { red, green, purple } from '@mui/material/colors';
//import EnvironmentDefs from '../../EnvironmentDefs';

import { disableAttributeCheck } from '../../utils/InputUtils';
import { getDayLabel } from '../../utils/InputUtils';

//const envDefs = new EnvironmentDefs();
const abEnvironment = new AbEnvironment();

const CustomerLookupQuery = graphql`
query CustomerLookupQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    parentId
    accountType
    accountNum
    accountName
    description
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    stripeCid
    dwollaCid
    hubspotId
    achActive
    accountStatus
    customerStatus
    sendMonthlyStatement
    billingDayOfMonth
    maxCharge
    floatLimit
    requestedFloatLimit
    xferAssentBy
    useBudgets
    latestBudgetApproved
    mccAuth
    mccWhitelist
    lastSoSent
    plaidCheckPercAllow
    achLimitNopeek
    achLimitAbsolute
    achPercAvailableOnCharge
    achMinAvailableOnCharge
    ccPercAvailableOnCharge
    ccMinAvailableOnCharge
    walletTsByAccountId {
      totalCount
    }
    accountTByParentId {
      accountStatus
      useBudgets
      appTsByAccountId {
        nodes {
          appId
          appName
        }
      }
      accountDelegatedAuthsTsByAccountId(condition: {appName: "FreshBooks"}) {
        totalCount
        nodes {
          appIntegrationTByAppIntegrationId {
            appIntegrationId
            appLogo
            appLogoWide
            accountIntegrationTsByAppIntegrationId {
              nodes {
                entityId
              }
            }
          }
        }
      }
    }
    projectTsByCustomerAccountId(orderBy: PROJECT_NAME_ASC) {
      totalCount
      nodes {
        projectId
        projectName
        walletTByCustomerWalletId {
          walletId
        }
      }
    }
    accountIntegrationTsByAccountId {
      nodes {
        accountIntegrationId
        appIntegrationId
        entityId
        environment
        aiStatus
      }
    }
    paymentSourceTsByAccountId(orderBy: PS_ORDER_ASC) {
      totalCount
      nodes {
        psId
        psOrder
        extSource
        extPsId
      }
    }
    customerServiceTsByCustomerAccountId {
      totalCount
    }
    customerAppTsByCustomerAccountId {
      nodes {
        customerAppId
        parentAppId
        appId
        bcId
        appStatus
        bindCardTByBcId {
          extSource
          extBcId
          bcStatus
          firstName
          lastName
          address1
          city
          state
          country
          postalCode
        }
        atId
        assetTypeTByAtId {
          atCode
          atSymbol
          placement
        }
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        appStatus
        notes
        paymentOrder
        marqetaMid
        appTByAppId {
          parentAppId
          oemAppId
          appUrl
          appLogo
          appName
          appSku
          appType
        }
      }
    }
    userTsByAccountId(first: 20) {
      totalCount
      nodes {
        userId
        userName
        userRole
        lastName
        firstName
        email
        phone
        address1
        address2
        city
        state
        country
        postalCode
        marqetaUid
        userArole
        userConfirmDate
        termsAcceptDate
        auth0Uid
      }
    }
  }
}
`;

const styleSheet = theme => ({
  tabroot: {
    flexGrow: 1,
    margin: 0,
    width: '100%',
    //backgroundColor: theme.palette.primary.light,
    borderBottom: '2px solid #dedede'
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left'
  },
  integrationPaper: {
    padding: theme.spacing(2),
    textAlign: 'left',
  },
  clientSelect: {
    width: 344,
  },
  avatar: {
    marginRight: 20,
    float: 'left',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.text.secondary
  },
  formControl: {
    margin: 'theme.spacing(1)',
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconbutton: {
    width: 120,
    height: 34,
    marginRight: 20,
  },
  editicon: {
    width: 22,
    height: 22,
  },
  statusButton: {
    color: '#444'
  },
  tblHead: {
    backgroundColor: '#f1f1f1'
  },
  button: {
    marginTop: 16,

  },
  tabScrollButton: {
    flex: '0 0 0px'
  },
  percentField: {
    marginRight: theme.spacing(1),
    width: 135,
  },

  /* Hidden below is sort of a hack.  Without overflow hidden - scroll bars appear but were not revealing 
  more data.  It was not obvious what is causing it.  Needs more research */

  /* Screen size specific styles */
  [theme.breakpoints.up('xs')]: {
    tabContainer: {
      padding: 0,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'xx-small',
    },
    labelContainer: {
      paddingLeft: 4,
      paddingRight: 4,
    },
    label: {
      fontSize: 'xx-small',
    },
    labelWrapped: {
      fontSize: 'xx-small',
    },
  }
  ,
  [theme.breakpoints.up('sm')]: {
    tabContainer: {
      padding: 8,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'x-small',
    },
    labelContainer: {
      paddingLeft: 8,
      paddingRight: 8,
    },
    label: {
      fontSize: 'x-small',
    },
    labelWrapped: {
      fontSize: 'x-small',
    },
  },
  [theme.breakpoints.up('md')]: {
    tabContainer: {
      padding: 20,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: '0.75rem',
    },
    alsPaper: {
      padding: theme.spacing(1),
      // width: 200
    },
    labelContainer: {
      paddingLeft: 12,
      paddingRight: 12,
    },
    label: {
      fontSize: '0.75rem',
    },
    labelWrapped: {
      fontSize: '0.75rem',
    },
  }
});

function TabContainer(props) {
  const classes = props.classes;
  return <div className={classes.tabContainer}>{props.children}</div>;
}

class Customer extends Component {

  constructor(props) {
    super(props);

    this.state = {
      value: 0,
      subscriptionTabValue: 0,
      showSpinner: false,
      showSecondarySpinner: false,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,
      accountId: null,
      accountName: null,
      accountNum: null,
      description: null,
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      country: null,
      postalCode: null,
      stripeCid: null,
      dwollaCid: null,
      sendMonthlyStatement: null,
      custApps: [],
      mccAuth: null,
      mccWhitelist: null,
      billingDayOfMonth: null,
      accountStatus: null,
      maxCharge: null,
      floatLimit: null,
      requestedFloatLimit: null,
      plaidCheckPercAllow: null,
      achLimitNopeek: null,
      achLimitAbsolute: null,
      achPercAvailableOnCharge: null,
      achMinAvailableOnCharge: null,
      ccPercAvailableOnCharge: null,
      ccMinAvailableOnCharge: null,
      showAdminSavingSpinner: false,
      showTopupSpinner: false,
      showReactivateSpinner: false,
 
      alsAssetBalance: null,
      alsPayInAr: null,
      alsAssetAndFullArBalance: null,
      alsPayInArAvailable: null,
      alsAssetAndArBalance: null,
      alsAssetArAuthBalance: null,
      alsBalanceForTopupCalc: null,
      alsLiabilityBalance: null,
      alsEquityBalance: null,
      alsMaxPayoutBalance: null,
      alsAuthBalance: null,
      alsFloatBalanceDisplay: null,
      alsFloatBalanceForAuth: null,

      anchorEl: null,
      confirmDelete: false,
      confirmSuspend: false,
      confirmReenable: false,
      confirmTransfer: false,
      transferConfirmed: false,
      confirmUserUpgrade: false,
      confirmDeleteSubs: false,

      fbClientId: null,
      fbClients: null,

      //admin action state values.
      acctCredit: 0.00,
      creditNotes: 'Account credit',

      forceFetch: 0
    }

    this.loadFbClients = null;
  }

  componentDidMount() {
    const { isAuthenticatedAppBind, isAuthenticatedReseller } = this.props.auth;
    if (isAuthenticatedReseller() || isAuthenticatedAppBind()) {
      if (abEnvironment.isSegmentActive()) { window.analytics.page(); }

      //look for a special url value to pre-position on subs.
      const params = new URLSearchParams(this.props.location.search);
      let tab = params.get('tab');
      if (tab === 'subs') {
        localStorage.setItem('customerTabVal', 3);
      }

      // Is there something in local storage?
      let theValue = 0;
      let tabval = localStorage.getItem('customerTabVal');
      if (tabval !== null) {
        theValue = parseInt(tabval, 10);
      }

      // 

      // Is FreshBooks active?
      IntegrationCountFetch.fetch(abEnvironment.getUserAttribute('a_id'), "FreshBooks").toPromise()
      .then(icf => {
        let theAccount = icf.accountTByAccountId;
        if (theAccount != null && theAccount.accountDelegatedAuthsTsByAccountId != null && theAccount.accountDelegatedAuthsTsByAccountId.totalCount > 0) {
          // Attempt to get the list of clients.  If there is no integration, we'll get an error.
          return this.getFreshBooksClients();
        } else {
          return null;
        }
      })
      .then(result => {
        if (result != null) {
          let fbClients = result;
          this.setState({ value: theValue, fbClients: fbClients });
        } else {
          this.setState({ value: theValue });
        }
      })
      .catch(err => {
        this.setState({ value: theValue });
      });

      if (isAuthenticatedAppBind()) {
        let acctId = params.get('accountId');
        AccountLedgerSummaryFetch.fetch(acctId).toPromise()
        .then(aef => {
          this.setState({
            alsAssetBalance: aef.getaccountledgersummary.assetBalance,
            alsPayInAr: aef.getaccountledgersummary.alsPayInAr,
            alsAssetAndFullArBalance: aef.getaccountledgersummary.assetAndFullArBalance,
            alsPayInArAvailable: aef.getaccountledgersummary.alsPayInArAvailable,
            alsAssetAndArBalance: aef.getaccountledgersummary.assetAndArBalance,
            alsAssetArAuthBalance: aef.getaccountledgersummary.assetArAuthBalance,
            alsBalanceForTopupCalc: aef.getaccountledgersummary.alsBalanceForTopupCalc,
            alsLiabilityBalance: aef.getaccountledgersummary.liabilityBalance,
            alsEquityBalance: aef.getaccountledgersummary.equityBalance,
            alsMaxPayoutBalance: aef.getaccountledgersummary.maxPayoutBalance,
            alsAuthBalance: aef.getaccountledgersummary.authBalance,
            alsFloatBalanceDisplay: aef.getaccountledgersummary.alsFloatBalanceDisplay,
            alsFloatBalanceForAuth: aef.getaccountledgersummary.alsFloatBalanceForAuth
          });
        })
        .catch(err => {
          //nothing to do....
        });
      }
    }
  }

  getFreshBooksClients() {
    const { callPartnerApi } = this.props.auth;

    let payload = {
      operation: "FreshBooksListClients",
      params: {
        email: null,
        companyName: null
      }
    };

    return new Promise(function (resolve, reject) {
      return callPartnerApi('appbind/generic',
        'POST',
        payload
      )
        .then(resp => {
          return resp.json();
        })
        .then(response => {
          if (response != null && response.body != null && response.body.body != null && response.body.body.response != null) {
            resolve(response.body.body.response.result.clients);
          } else {
            // Throw
            throw (response);
          }
        })
        .catch(error => {
          //logError('Exception in Customer.js: ' + error);
          reject(error);
        });
    });
  }

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  }
  handleMenuClick = event => {
    this.setState({ anchorEl: event.currentTarget });
  }

  handleChange = event => {
    this.setState({ name: event.target.value });
  };

  handleTabChange = (event, value) => {
    this.setState({ value });
    localStorage.setItem('customerTabVal', value);
  }

  setTabValue = (tv) => {
    this.setState({ value: tv });
    localStorage.setItem('customerTabVal', tv);
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  // 
  goToUserCommunications(primaryUser, route, propsToCache) {
    if (primaryUser === false) {
      this.goToUserCreateNonPrimary(route, propsToCache);
    } else {
      this.goToUserCreate(route, propsToCache);
    }
  }

  goToUserCreateNonPrimary(route, propsToCache) {
    //here it is not the primary user, just pass the company address.
    let param = {
      'address1': propsToCache.address1,
      'address2': propsToCache.address2,
      'city': propsToCache.city,
      'state': propsToCache.state,
      'country': propsToCache.country,
      'postalCode': propsToCache.postalCode
    };

    localStorage.setItem('userCreateParamProps', JSON.stringify(param));
    this.props.history.push(`/${route}`);
  }

  goToUserCreate(route, propsToCache) {
    localStorage.setItem('userCreateParamProps', JSON.stringify(propsToCache));
    this.props.history.push(`/${route}`);
  }

  goToSO(props) {
    let propsToCache = {
      fromRequestApproval: true,
      accountId: props.accountId,
      companyName: props.accountName,
      contactLastName: props.contactLastName,
      contactFirstName: props.contactFirstName,
      contactEmail: props.contactEmail
    };

    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));

    this.props.history.push(`/subscriptionorder`);
  }

  getValueForRender(state_member, prop_member, theDefault) {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  disableAccountSave(accountProps, savedFbClientId) {
    if (this.disableAccountAttributeSave(accountProps) && !this.isFbClientIdChanged(savedFbClientId)) {
      return true;
    } else {
      return false;
    }
  }

  disableAccountAttributeSave(accountProps) {
    const { validateEmail } = this.props.auth;

    if (accountProps === null)
      return true;

    //Let's make sure that address was no blanked out if it was entered before.
    if (accountProps.address1 && this.state.address1 === '') {
      return true;
    }
    if (accountProps.city && this.state.city === '') {
      return true;
    }
    if (accountProps.state && this.state.state === '') {
      return true;
    }
    if (accountProps.country && this.state.country === '') {
      return true;
    }
    if (accountProps.postalCode && this.state.postalCode === '') {
      return true;
    }

    //if it got here, that means either the address was not changed or it never was entered.  Now, lets make sure
    //if they enter any of the address components, it is all entered.
    if (!accountProps.address1 && !accountProps.city && !accountProps.state && !accountProps.country && !accountProps.postalCode) {
      let oneBlank = false;
      let oneEntered = false;
      if (this.state.address1 === null || this.state.address1 === '') {
        oneBlank = true;
      } else {
        oneEntered = true;
      }
      if (this.state.city === null || this.state.city === '') {
        oneBlank = true;
      } else {
        oneEntered = true;
      }
      if (this.state.state === null || this.state.state === '') {
        oneBlank = true;
      } else {
        oneEntered = true;
      }
      if (this.state.country === null || this.state.country === '') {
        oneBlank = true;
      } else {
        oneEntered = true;
      }
      if (this.state.postalCode === null || this.state.postalCode === '') {
        oneBlank = true;
      } else {
        oneEntered = true;
      }
      if (oneEntered && oneBlank) {
        return true;
      }
    }

    let retval = disableAttributeCheck(this.state.accountName, accountProps.accountName) &&
      this.disableAttributeCheckNonRequired(this.state.accountNum, accountProps.accountNum) &&
      this.disableAttributeCheckNonRequired(this.state.description, accountProps.description) &&
      disableAttributeCheck(this.state.firstName, accountProps.contactFirstName) &&
      disableAttributeCheck(this.state.lastName, accountProps.contactLastName) &&
      disableAttributeCheck(this.state.email, accountProps.contactEmail) &&
      //this.disableAttributeCheck(this.state.phone,       accountProps.contactPhone) &&
      disableAttributeCheck(this.state.address1, accountProps.address1) &&
      this.disableAttributeCheckNonRequired(this.state.address2, accountProps.address2) &&
      disableAttributeCheck(this.state.city, accountProps.city) &&
      disableAttributeCheck(this.state.state, accountProps.state) &&
      disableAttributeCheck(this.state.country, accountProps.country) &&
      disableAttributeCheck(this.state.postalCode, accountProps.postalCode) &&
      disableAttributeCheck(this.state.sendMonthlyStatement, accountProps.sendMonthlyStatement);

    if (!retval) {
      // Check email
      if (this.state.email != null && this.state.email !== '') {
        let emailOk = validateEmail(this.state.email);
        if (!emailOk)
          retval = true;
      }
    }

    return retval;
  }

  disableAdminAccountSave(accountProps) {
    if (accountProps === null)
      return true;

    //console.log(this.disableMCCWhitelist(accountProps));

    if (this.disableMCCWhitelist(accountProps)) {
      return true;
    }

    let retval = disableAttributeCheck(this.state.mccAuth,  accountProps.mccAuth) &&
                  this.disableAttributeCheckNonRequired(this.state.mccWhitelist, accountProps.mccWhitelist) &&
                  disableAttributeCheck(this.state.accountStatus, accountProps.accountStatus) &&
                  disableAttributeCheck(this.state.billingDayOfMonth, accountProps.billingDayOfMonth) &&
                  disableAttributeCheck(this.state.maxCharge, accountProps.maxCharge) &&
                  disableAttributeCheck(this.state.floatLimit, accountProps.floatLimit) &&
                  disableAttributeCheck(this.state.requestedFloatLimit, accountProps.requestedFloatLimit) &&
                  disableAttributeCheck(this.state.plaidCheckPercAllow, accountProps.plaidCheckPercAllow) &&
                  disableAttributeCheck(this.state.achLimitNopeek, accountProps.achLimitNopeek) &&
                  disableAttributeCheck(this.state.achLimitAbsolute, accountProps.achLimitAbsolute) &&
                  disableAttributeCheck(this.state.achPercAvailableOnCharge, accountProps.achPercAvailableOnCharge) &&
                  disableAttributeCheck(this.state.achMinAvailableOnCharge, accountProps.achMinAvailableOnCharge) &&
                  disableAttributeCheck(this.state.ccPercAvailableOnCharge, accountProps.ccPercAvailableOnCharge) &&
                  disableAttributeCheck(this.state.ccMinAvailableOnCharge, accountProps.ccMinAvailableOnCharge);

    if (!retval) {
      if (this.getValueForRender(this.state.requestedFloatLimit, accountProps.requestedFloatLimit,  0) > 
        this.getValueForRender(this.state.floatLimit, accountProps.floatLimit,  0)) {
          retval = true;
      }
    }

    return retval;

  }

  disableCreditButton() {
    let creditVal = Number(this.state.acctCredit);
    if (creditVal === 0) {
      return true;
    }
    return false;
  }

  disableMCCWhitelist(accountProps) {

    const regex = new RegExp('^[0-9]*$');

    if (accountProps.mccWhitelist) {
      if (this.state.mccWhitelist === '') {
        //user blanks out previous value, all good
        return false;
      }
    } 
    
    //if user entered a value, make it conforms to NNNN or NNNN,NNNN, etc.  Cannot end in a comma
    if (this.state.mccWhitelist && this.state.mccWhitelist.length > 0) {
      if (this.state.mccWhitelist.length === 4) {
        //input exactly 4, test that it is all numbers
        if (regex.test(this.state.mccWhitelist)) {
          return false;
        } else {
          return true;
        }
      } else if (this.state.mccWhitelist.length > 4) {
        //split the comma seperated list and test each chunk
        const mccCodes = this.state.mccWhitelist.split(",");
        let i = 0;
        for (i = 0; i < mccCodes.length; i++) {
          let mccCode = mccCodes[i];
          if (mccCode.length === 4) {
            if (regex.test(mccCode)) {
              //this one is good, fall to next
            } else {
              //if any not good, disable.
              return true;
            }
    
          } else {
            return true;
          }
        }
        return false;
      } else {

        return true;
      }
    } else {
      return false;
    }

  }


  isFbClientIdChanged(savedFbClientId) {
    // The trick is that if the values are numbers, we want to compare as numbers.
    if (this.state.fbClientId != null && this.state.fbClientId !== 'UNSET') {
      let fbClientIdNum = Number(this.state.fbClientId);
      let savedFbClientIdNum = Number(savedFbClientId);
      if (Number.isNaN(fbClientIdNum) || Number.isNaN(savedFbClientIdNum)) {
        // Non-numeric comparison
        if (this.state.fbClientId !== savedFbClientId) {
          return true;
        } else {
          return false;
        }
      }

      if (fbClientIdNum !== savedFbClientIdNum) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  disableAttributeCheckNonRequired(stateVar, propsVar) {
    if (stateVar !== null) {
      if (propsVar === null && stateVar === '') {
        return true;
      }
      if (propsVar !== stateVar) {
        return false;
      }
    }
    return true;
  }

  getAccountNumberFieldLabel(propsAccountNumber) {
    if (this.state.accountNum || propsAccountNumber !== '') {
      return "Account identifier";
    } else {
      return "An optional internal name";
    }
  }

  getAccountNumberHelperText(propsAccountNumber) {
    if (this.state.accountNum || propsAccountNumber !== '') {
      return "An optional internal name or number for your customer";
    } else {
      return "";
    }
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  showSecondarySpinner() {
    return this.state.showSecondarySpinner;
  }

  setCountry = (country) => { this.setState({ country: country }); }
  setRegion = (region) => { this.setState({ state: region }); }
  setCity = (city) => { this.setState({ city: city }); }
  setPostalCode = (postalCode) => { this.setState({ postalCode: postalCode }); }
  setAddress1 = (address1) => { this.setState({ address1: address1 }); }
  setAddress2 = (address2) => { this.setState({ address2: address2 }); }

  masqueradeAs(u_id, a_id, role, userName) {
    this.props.auth.masqueradeAs(u_id, a_id, role, userName);
  }

  getCurrencyDisplayValue(v) {
    let value = 0;
    if (v) {
      value = v;
    }
    let displayValue;
    try {
      displayValue = Number(value).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    } catch (err) {
      displayValue = Number(0).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    }

    return displayValue;
  }

  render() {
    const { validateEmail, isAuthenticated, isAuthenticatedAppBind, isAuthenticatedReseller, isAdminUser, isAccountSuspended, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    const cancelColor = red[300];
    const okColor = green[300];
    const suspendedColor = red[300];
    const transferColor = purple[800];

    let value = this.state.value;

    // Experiences for different personas...
    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedReseller() || isAuthenticatedAppBind()) {
      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');
      let fp = params.get('fp');

      let backText = "Accounts";
      if (isAuthenticatedReseller()) {
        backText = "Customers";

        //if user coming from subscription manage, set the back text differently
        var subscriptionReturn = localStorage.getItem('subscriptionReturn');
        if (subscriptionReturn) {
          backText = "Subscription";
        }
      }

      // Transfer feature flag
      // - the transfer flag is no longer used.
      // - TODO - remove flag, and just have on
      let newTransfer = true;

      if (fp != null && fp !== '') {
        backText = fp;
      }
    
      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={CustomerLookupQuery}
          variables={{ accountId: acctId, forceFetch: this.state.forceFetch }}
          render={({ error, props }) => {
            if (error) {
              logError(error.message);
              return (
                <ServerError />
              );
            } else if (props) {
              if (props.accountTByAccountId === null || props.accountTByAccountId.accountType !== 4) {
                return (
                  <PageTitle title="Customer not found" showBackButton={true} name={backText} history={this.props.history}/>
                );
              }

              let theAccount = props.accountTByAccountId;

              //here we need to figure out if the user is one of us but NOT masquarading.
              let isUserAppBind = (abEnvironment.getUserAttribute("role") === "appbind_appbind");

              //
              // Discrete settings
              // - showSO should be on for all soon, but leave as-is for a bit longer
              // - showServices should be governed by feature flag
              // - showExpensing is something we'll want on a feature flag eventually.  If on, we'll have tab 3 be either wallets or charge limits,
              //   and tab 4 be reports.  If off, we won't have tab 3 and 4.  For now, we should base this on whether or not there are transactions.
              // - showWallet is a sub-category of expensing.
              //
              let showSOs = false;
              let showWallet = false;

              // Expensing
              let showExpensing = false;
              let expensingActive = false;
              let expensing = abEnvironment.getFlagValue("expensing");
              if (expensing != null && expensing.flagValue === 1) {
                expensingActive = true;
              }          
              if (theAccount.paymentSourceTsByAccountId.totalCount > 0 || expensingActive === true) {
                showExpensing = true;
              }

              // Check for bad tab value
              if (showExpensing === false && value > 1) {
                value = 1;
              }

              // Services
              let showServices = false;
              let serviceBillingActive = false;
              let serviceBilling = abEnvironment.getFlagValue("serviceBilling");
              if (serviceBilling != null && serviceBilling.flagValue === 1) {
                serviceBillingActive = true;
              }          
              if (theAccount.customerServiceTsByCustomerAccountId.totalCount > 0 || serviceBillingActive === true) {
                showServices = true;
              }

              if (isAuthenticatedReseller()) {
                if (abEnvironment.getUserAttribute('a_useBudgets') === '2') {
                  showSOs = true;
                  showWallet = true;
                } else if (abEnvironment.getUserAttribute('a_useBudgets') === '1') {
                  // Depends on customer setting
                  if (theAccount.useBudgets > 0) {
                    showSOs = true;
                    showWallet = true;
                  }
                }
              }

              // For AppBind admin
              if (isAuthenticatedAppBind() && theAccount.accountTByParentId.useBudgets > 0) {
                showWallet = true;
              }

              //Does the customer have a wallet?
              let hasWallet = false;
              if (theAccount.walletTsByAccountId.totalCount > 0) {
                hasWallet = true;
              }
              let viewLedgerLink = "accountledgers?accountId=" + theAccount.accountId +
              "&accountName=" + theAccount.accountName;

              let uid = abEnvironment.getUserAttribute('u_id'); // Not relevant here?
              let stripeCid = theAccount.stripeCid;
              let dwollaCid = theAccount.dwollaCid;
              let psIds = [];
              for (let i = 0; i < theAccount.paymentSourceTsByAccountId.nodes.length; i++) {
                let psId = theAccount.paymentSourceTsByAccountId.nodes[i].psId;
                psIds.push(psId);
              }

              let custAppLink = "subscriptioncreate?accountId=" + theAccount.accountId;
              let newUserLink = "usercreate?accountId=" + theAccount.accountId;

              let customerName = this.getValueForRender(this.state.accountName,
                theAccount.accountName, '');
              let customerInitials = customerName[0];

              let anyUserInvite = false;
              if (psIds.length === 0) {
                anyUserInvite = true;
              }

              let customerCancelled = false;
              let customerTransferred = false;
              let customerTransferring = false;
              if (theAccount.customerStatus === 1) {
                customerTransferring = true;
              }
              if (theAccount.customerStatus === 2) {
                customerTransferred = true;
              }
              if (theAccount.accountStatus === 3) {
                customerCancelled = true;
              }

              let primaryUserInvite = null;
              let invitationNeeded = false;
              if (theAccount.userTsByAccountId.totalCount === 0 && !customerTransferred && !customerCancelled) {
                invitationNeeded = true;
                primaryUserInvite = 'Invite ' +
                  this.getValueForRender(this.state.firstName,
                    theAccount.contactFirstName,
                    '') +
                  ' ' +
                  this.getValueForRender(this.state.lastName,
                    theAccount.contactLastName,
                    '') +
                  ' to AppBind';
              }

              //see if any of the users match the accounts contact.  This is important for transfer
              let contactIsAUser = false;
              for (let i = 0; i < theAccount.userTsByAccountId.nodes.length; i++) {
                if (theAccount.userTsByAccountId.nodes[i].email === theAccount.contactEmail) {
                  contactIsAUser = true;
                }
              }
              

              let deleteEligible = false;
              let noUsers = false;
              let users = [];
              //let upgradeAccountEligible = false;

              if (theAccount.userTsByAccountId.totalCount === 0) {
                //no users therefore no one to send assent to.
                noUsers = true;
              }

              if ((theAccount.paymentSourceTsByAccountId.nodes.length === 0) && (theAccount.customerAppTsByCustomerAccountId.nodes.length === 0)) {
                if (theAccount.userTsByAccountId.totalCount === 0) {
                  //no payment sources, subscriptions, or users
                  deleteEligible = true;
                } else {
                  //see if any of the users have accepted terms
                  users = theAccount.userTsByAccountId.nodes;
                  let loggedIn = false;
                  for (let i = 0; i < users.length; i++) {
                    if ((users[i].userConfirmDate != null) || (users[i].termsAcceptDate != null)) {
                      loggedIn = true;
                    }
                  }
                  if (!loggedIn) {
                    deleteEligible = true;
                  }

                }
              }

              /*if (theAccount.xferAssentBy) {
                //customer needs to assent account before reseller is able to upgrade.
                users = theAccount.userTsByAccountId.nodes;
                let loggedIn = false;
                for (let i = 0; i < users.length; i++) {
                   if (users[i].userArole === 'appbind_customer_unauth') {
                    upgradeAccountEligible = true;
                    break;
                  }
                }  
              }*/
              let resellerStatus = null;
              //if accountTByParentId is null, it means the customer has transferred.
              if (theAccount.accountTByParentId && theAccount.accountTByParentId.accountStatus) {
                resellerStatus = theAccount.accountTByParentId.accountStatus;
              }

              let suspendMessage = 'When a customer is suspended, subscription charges are declined, and no changes can be made to subscription or funding configuration.  Are you sure you want to proceed?';
              if (!deleteEligible) {
                if (theAccount.customerAppTsByCustomerAccountId.nodes.length > 0) {
                  suspendMessage = 'There ';
                  if (theAccount.customerAppTsByCustomerAccountId.nodes.length === 1) {
                    suspendMessage = suspendMessage + 'is ' + theAccount.customerAppTsByCustomerAccountId.nodes.length + ' subscription ';
                  } else {
                    suspendMessage = suspendMessage + 'are ' + theAccount.customerAppTsByCustomerAccountId.nodes.length + ' subscriptions ';
                  }
                  suspendMessage = suspendMessage + 'for this customer account. If you suspend this account, any active subscription charges will be declined, and you will not be able to make any changes to funding source or subscription configuration. Are you sure you want to suspend this customer account?';
                } else if (theAccount.userTsByAccountId.totalCount > 0) {
                  suspendMessage = 'There ';
                  if (theAccount.userTsByAccountId.totalCount > 0) {
                    suspendMessage = suspendMessage + 'is ' + theAccount.userTsByAccountId.totalCount + ' active user ';
                  } else {
                    suspendMessage = suspendMessage + 'are ' + theAccount.userTsByAccountId.totalCount + ' active users ';
                  }

                  suspendMessage = suspendMessage + 'in this customer account. If you suspend this account, customer users will not be able to make any changes to funding source configuration. Are you sure you want to suspend this customer account?';
                }
              }

              let upperRightInvite = 'Invite ' + customerName + ' to AppBind';
              let upperRightTooltip = 'Add and invite the primary account contact to AppBind.  This allows ' + customerName + ' to add a funding source and view subscriptions that you are managing.';

              let address = {
                country: this.getValueForRender(this.state.country, theAccount.country, ''),
                state: this.getValueForRender(this.state.state, theAccount.state, ''),
                city: this.getValueForRender(this.state.city, theAccount.city, ''),
                postalCode: this.getValueForRender(this.state.postalCode, theAccount.postalCode, ''),
                address1: this.getValueForRender(this.state.address1, theAccount.address1, ''),
                address2: this.getValueForRender(this.state.address2, theAccount.address2, '')
              };
              let regionLabel = "Region";

              //let emailHelperText = '';
              let emailError = false;
              if (this.state.email != null && this.state.email !== '') {
                let emailOk = validateEmail(this.state.email);
                if (!emailOk) {
                  //emailHelperText = 'Please enter a valid email address';
                  emailError = true;
                }
              }

              let transferConfirmationMsg = 'We’ll send an email to ' + this.getValueForRender(this.state.accountName, theAccount.accountName, '');
              transferConfirmationMsg = transferConfirmationMsg + " (" + this.getValueForRender(this.state.email, theAccount.contactEmail, '') + ") to start the process of transferring their subscriptions.  ";

              // For passing to funding source component
              let uInfo = {
                accountId: theAccount.accountId,
                accountName: theAccount.accountName,
                description: theAccount.description,
                firstName: theAccount.contactFirstName,
                lastName: theAccount.contactLastName,
                email: theAccount.contactEmail,
                phone: theAccount.contactPhone,
                address1: theAccount.address1,
                address2: theAccount.address2,
                city: theAccount.city,
                state: theAccount.state,
                country: theAccount.country,
                postalCode: theAccount.postalCode,
                dwollaCid: dwollaCid,
                stripeCid: stripeCid,
                achActive: theAccount.achActive
              };

              // For restricting a feature to POC only.  Uncomment to use.
              /*
              let pocEnvironment = false;
              if (envDefs.getEnv() === "poc" || envDefs.getEnv() === "devpoc") {
                pocEnvironment = true;
              } 
              */

              let freshBooksAddLabel = "Add " + customerName + " to FreshBooks";
              let freshBooksNoConnectLabel = "Don't connect " + customerName + " to FreshBooks";
              let freshBooksIntegrationActive = false;
              let freshBooksAppIntegrationId = null;
              let freshBooksLogo = null;
              // let freshBooksLogoWide = null;
              let theFbClientId = this.state.fbClientId;
              if (theFbClientId === "UNSET") {
                theFbClientId = null;
              }
              let savedClientId = null;
              let foundNameMatch = false;
              let fbClientsSorted = [];
              let appIntegration = null;

              let accountDelegatedAuths = null;
              if (theAccount.accountTByParentId != null) {
                accountDelegatedAuths = theAccount.accountTByParentId.accountDelegatedAuthsTsByAccountId;
              }
              if (accountDelegatedAuths != null && accountDelegatedAuths.totalCount >= 1) {
                freshBooksIntegrationActive = true;
                appIntegration = accountDelegatedAuths.nodes[0].appIntegrationTByAppIntegrationId;
                freshBooksAppIntegrationId = appIntegration.appIntegrationId;
                freshBooksLogo = appIntegration.appLogo;
                // freshBooksLogoWide = appIntegration.appLogoWide;

                // Get the saved client id
                let accountIntegrations = theAccount.accountIntegrationTsByAccountId.nodes;
                if (accountIntegrations.length > 0) {
                  for (let k = 0; k < accountIntegrations.length; k++) {
                    if (accountIntegrations[k].appIntegrationId === freshBooksAppIntegrationId) {
                      savedClientId = accountIntegrations[k].entityId;
                      break;
                    }
                  }
                }

                if (theFbClientId == null && savedClientId != null) {
                  theFbClientId = savedClientId;
                }
                if (theFbClientId == null) {
                  theFbClientId = '';
                }

                // The client list was retrieved in the mount method.  This should be sorted, and already
                // mapped clients should be omitted from the list.  To get the omissions, we'll look at the
                // accountIntegrationTsByAppIntegrationId
                if (this.state.fbClients != null) {
                  // Get the list of already mapped accounts
                  let accountIntegrations = []; // Default to empty array
                  if (appIntegration != null) {
                    accountIntegrations = appIntegration.accountIntegrationTsByAppIntegrationId.nodes;
                  }

                  // Look for clients to add to the list in 3 passes
                  // - Pass 1: If there is already a saved mapping, just add that one
                  // - Pass 2: Check the entire list for a fb client that matches the organization name.  If
                  //           there is such a match, only present that choice
                  // - Pass 3: Add all clients that don't have a mapping already
                  if (savedClientId != null) {
                    for (let k = 0; k < this.state.fbClients.length; k++) {
                      let aClient = this.state.fbClients[k];

                      // If this is the currently saved client, add it
                      if (Number(aClient.id) === Number(savedClientId)) {
                        fbClientsSorted.push(aClient);
                        break;
                      }
                    }
                  } else {
                    for (let k = 0; k < this.state.fbClients.length; k++) {
                      let aClient = this.state.fbClients[k];

                      // If the name matches, add it - this is the only name we'll use.
                      if (aClient.organization === customerName) {
                        // If this client is already mapped, it needs to be excluded.  This shouldn't happen,
                        // but protect against it nevertheless.
                        let mappingExists = false;
                        for (let m = 0; m < accountIntegrations.length; m++) {
                          let ai = accountIntegrations[m];
                          if (Number(ai.entityId) === Number(aClient.id)) {
                            mappingExists = true;
                            break;
                          }
                        }

                        if (! mappingExists) {
                          foundNameMatch = true;
                          fbClientsSorted.push(aClient);
                          break;
                        }
                      }
                    }

                    // Now that there are no special cases, show all non-mapped clients as options
                    if (! foundNameMatch) {
                      for (let k = 0; k < this.state.fbClients.length; k++) {
                        let aClient = this.state.fbClients[k];

                        let mappingExists = false;
                        for (let m = 0; m < accountIntegrations.length; m++) {
                          let ai = accountIntegrations[m];
                          if (Number(ai.entityId) === Number(aClient.id)) {
                            mappingExists = true;
                            break;
                          }
                        }

                        if (! mappingExists) {
                          fbClientsSorted.push(aClient);
                        }
                      }
                    }
                  }

                  if (fbClientsSorted.length > 1) {
                    // Sort by organization
                    fbClientsSorted.sort(function (a, b) {
                      let aStr = '' + a.organization;
                      let bStr = '' + b.organization;
                      return aStr.localeCompare(bStr);
                    });
                  }
                }
              }

              let commsEmailSwitchDisabled = false;
              if (customerTransferred === true || primaryUserInvite != null || customerCancelled === true) {
                commsEmailSwitchDisabled = true;
              }

              if (customerTransferred) {
                //this could happen if the user switching between transferred and not.
                if (value === 1 || value === 2 || value === 3) {
                  value = 0;
                }
              }

              return (
                <Grid container spacing={3} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <PageTitle title="Customer details" showBackButton={true} name={backText} noMargin={true} history={this.props.history}/>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="space-between" style={{ marginBottom: '10px' }}>
                        <Grid item>
                          <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                            <Grid item>
                              <Avatar className={classes.avatar}>{customerInitials}</Avatar>
                            </Grid>
                            <Grid item>
                              <Typography variant="h5">{customerName}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        {(theAccount.accountStatus === 0) && (theAccount.customerStatus === 0) &&
                          <Grid item>
                            <Button color="secondary"
                              aria-owns={this.state.anchorEl ? 'simple-menu' : undefined}
                              aria-haspopup='true'
                              onClick={this.handleMenuClick}
                              className={classes.statusButton}
                            >
                              {/* <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><Hidden only={['xs', 'md', 'lg', 'xl']}>&nbsp;Act&nbsp;</Hidden><ArrowDropDown /> */}
                              <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><ArrowDropDown />
                            </Button>
                            <Menu
                              id="action-menu"
                              anchorEl={this.state.anchorEl}
                              open={Boolean(this.state.anchorEl)}
                              onClose={this.handleMenuClose}>

                              <MenuItem onClick={() => { this.setState({ anchorEl: null, confirmSuspend: true }); }}>
                                <ListItemIcon>
                                  <PauseCircleFilled htmlColor={suspendedColor} />
                                </ListItemIcon>
                                <ListItemText primary="Suspend" />
                              </MenuItem>
                              {deleteEligible &&
                                <MenuItem onClick={() => { this.setState({ anchorEl: null, confirmDelete: true }); }}>
                                  <ListItemIcon>
                                    <Delete htmlColor={suspendedColor} />
                                  </ListItemIcon>
                                  <ListItemText primary="Delete" />
                                </MenuItem>
                              }
                              {noUsers &&
                                <MenuItem disabled={isUserAppBind}>

                                  <ListItemIcon>
                                    <SubdirectoryArrowRight htmlColor={transferColor} />
                                  </ListItemIcon>
                                  <Tooltip title="Please invite your customer before transferring">
                                    <ListItemText primary="Transfer control" />
                                  </Tooltip>
                                </MenuItem>
                              }
                              {!noUsers &&
                                <MenuItem disabled={isUserAppBind} onClick={() => { this.setState({ anchorEl: null, confirmTransfer: true }); }}>
                                  <ListItemIcon>
                                    <SubdirectoryArrowRight htmlColor={transferColor} />
                                  </ListItemIcon>
                                  <ListItemText primary="Transfer control" />
                                </MenuItem>
                              }
                              {/*<MenuItem disabled={!upgradeAccountEligible} onClick={() => {this.setState({anchorEl: null, confirmUserUpgrade:true}); }}>
                                  <ListItemIcon>
                                    <HowToReg htmlColor={okColor}/>
                                  </ListItemIcon>
                                  <ListItemText primary="Upgrade account"/>
                                </MenuItem>*/}
                              {/*<MenuItem disabled={true} onClick={() => {this.setState({anchorEl: null, confirmDeleteSubs:true}); }}>
                                  <ListItemIcon>
                                    <Cancel htmlColor={cancelColor}/>
                                  </ListItemIcon>
                                  <ListItemText primary="Cancel all subscriptions"/>
                                </MenuItem>*/}
                            </Menu>
                          </Grid>
                        }

                        {(theAccount.accountStatus === 0) && (customerTransferred) &&
                          <Grid item>
                            <Tooltip title="Customer transferred">
                              <TransferWithinAStationIcon htmlColor={okColor} />
                            </Tooltip>
                          </Grid>
                        }
                        {(theAccount.accountStatus === 3) &&
                          <Grid item>
                            <Tooltip title="Customer canceled">
                              <Cancel htmlColor={cancelColor} />
                            </Tooltip>
                          </Grid>
                        }

                        {(theAccount.accountStatus === 0) && (customerTransferring) &&
                          <Grid item>
                            <Button color="secondary"
                              aria-owns={this.state.anchorEl ? 'simple-menu' : undefined}
                              aria-haspopup='true'
                              onClick={this.handleMenuClick}
                              className={classes.statusButton}
                            >
                              {/* <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><Hidden only={['xs', 'md', 'lg', 'xl']}>&nbsp;Act&nbsp;</Hidden><ArrowDropDown /> */}
                              <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active (transferring)&nbsp;</Hidden><ArrowDropDown />
                            </Button>
                            <Menu
                              id="action-menu"
                              anchorEl={this.state.anchorEl}
                              open={Boolean(this.state.anchorEl)}
                              onClose={this.handleMenuClose}>

                              <MenuItem onClick={() => { this.setState({ anchorEl: null, confirmSuspend: true }); }}>
                                <ListItemIcon>
                                  <PauseCircleFilled htmlColor={suspendedColor} />
                                </ListItemIcon>
                                <ListItemText primary="Suspend" />
                              </MenuItem>
                              <MenuItem disabled={noUsers} onClick={() => { this.setState({ anchorEl: null, confirmTransfer: true }); }}>
                                <ListItemIcon>
                                  <SubdirectoryArrowRight htmlColor={transferColor} />
                                </ListItemIcon>
                                <ListItemText primary="Resend transfer assent" />
                              </MenuItem>
                              {/*<MenuItem disabled={!upgradeAccountEligible} onClick={() => {this.setState({anchorEl: null, confirmUserUpgrade:true}); }}>
                                  <ListItemIcon>
                                    <HowToReg htmlColor={okColor}/>
                                  </ListItemIcon>
                                  <ListItemText primary="Upgrade account"/>
                                  </MenuItem>*/}
                            </Menu>
                          </Grid>
                        }

                        {(theAccount.accountStatus === 1) && (theAccount.customerStatus === 0) &&
                          <Grid item>
                            <Button color="secondary"
                              aria-owns={this.state.anchorEl ? 'simple-menu' : undefined}
                              aria-haspopup='true'
                              onClick={this.handleMenuClick}
                              className={classes.statusButton}
                            >
                              {/* <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><Hidden only={['xs', 'md', 'lg', 'xl']}>&nbsp;Act&nbsp;</Hidden><ArrowDropDown /> */}
                              <PauseCircleFilled htmlColor={cancelColor} /><Hidden only={['xs', 'sm']}>&nbsp;Suspended&nbsp;</Hidden><ArrowDropDown />
                            </Button>
                            <Menu
                              id="action-menu"
                              anchorEl={this.state.anchorEl}
                              open={Boolean(this.state.anchorEl)}
                              onClose={this.handleMenuClose}>

                              <MenuItem onClick={() => { this.setState({ anchorEl: null, confirmReenable: true }); }}>
                                <ListItemIcon>
                                  <PlayCircleFilled htmlColor={okColor} />
                                </ListItemIcon>
                                <ListItemText primary="Unsuspend" />
                              </MenuItem>
                              <MenuItem disabled={noUsers} onClick={() => { this.setState({ anchorEl: null, confirmTransfer: true }); }}>
                                <ListItemIcon>
                                  <SubdirectoryArrowRight htmlColor={transferColor} />
                                </ListItemIcon>
                                <ListItemText primary="Transfer control" />
                              </MenuItem>
                              <MenuItem disabled={true} onClick={() => { this.setState({ anchorEl: null, confirmDeleteSubs: true }); }}>
                                <ListItemIcon>
                                  <Cancel htmlColor={cancelColor} />
                                </ListItemIcon>
                                <ListItemText primary="Cancel all subscriptions" />
                              </MenuItem>
                            </Menu>
                          </Grid>
                        }

                        {(theAccount.accountStatus === 1) && (customerTransferring) &&
                          <Grid item>
                            <Button color="secondary"
                              aria-owns={this.state.anchorEl ? 'simple-menu' : undefined}
                              aria-haspopup='true'
                              onClick={this.handleMenuClick}
                              className={classes.statusButton}
                            >
                              {/* <CheckCircle htmlColor={okColor} /><Hidden only={['xs', 'sm']}>&nbsp;Active&nbsp;</Hidden><Hidden only={['xs', 'md', 'lg', 'xl']}>&nbsp;Act&nbsp;</Hidden><ArrowDropDown /> */}
                              <PauseCircleFilled htmlColor={cancelColor} /><Hidden only={['xs', 'sm']}>&nbsp;Suspended (transferring)&nbsp;</Hidden><ArrowDropDown />
                            </Button>
                            <Menu
                              id="action-menu"
                              anchorEl={this.state.anchorEl}
                              open={Boolean(this.state.anchorEl)}
                              onClose={this.handleMenuClose}>

                              <MenuItem onClick={() => { this.setState({ anchorEl: null, confirmReenable: true }); }}>
                                <ListItemIcon>
                                  <PlayCircleFilled htmlColor={okColor} />
                                </ListItemIcon>
                                <ListItemText primary="Unsuspend" />
                              </MenuItem>
                            </Menu>
                          </Grid>}

                        {primaryUserInvite != null && psIds.length === 0 &&
                          <Grid item xs={12}>
                            <Tooltip title={upperRightTooltip}>
                              <Button
                                color="primary"
                                variant="contained"
                                disabled={(theAccount.accountStatus === 1) || isAccountSuspended()}
                                onClick={() => { this.goToUserCommunications(true, newUserLink, theAccount); }}>
                                <Add /> {upperRightInvite}
                              </Button>
                            </Tooltip>
                          </Grid>}
                      </Grid>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={value}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{ root: classes.tabroot, scrollButtons: classes.tabScrollButton }}
                          >
                            <Tab label="Contact" icon={<Contacts />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            <Tab label="Subscriptions" icon={<CloudQueue />} disabled={customerTransferred || customerCancelled}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />
                            {showExpensing === true &&
                            <Tab label="Funding" icon={<CreditCard />} disabled={customerTransferred || customerCancelled}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                            {showExpensing === true && showWallet === true &&
                            <Tab label={"Expense wallet"} icon={<Wallet />} disabled={customerTransferred || customerCancelled}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                            {showExpensing === true && showWallet === false &&
                            <Tab label={"Charge limits"} icon={<EvStation />} disabled={customerTransferred || customerCancelled}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                            {showExpensing === true &&
                            <Tab label="Reports" icon={<MonetizationOn />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                          </Tabs>
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: '10px' }}>
                          {value === 0 &&
                          <TabContainer {...this.props}>
                            <Grid container spacing={2} justifyContent="flex-start">
                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitleFirst}>
                                  Account
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="customer-name" label="Customer (Business) Name *"
                                  value={this.getValueForRender(this.state.accountName,
                                    theAccount.accountName,
                                    '')}
                                  onChange={(e) => this.setState({ accountName: e.target.value })}
                                  inputProps={{ maxLength: 64 }}
                                  InputProps={{ readOnly: customerTransferred || customerCancelled, disableUnderline: customerTransferred || customerCancelled }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="account-num" label={this.getAccountNumberFieldLabel(theAccount.accountNum)}
                                  value={this.getValueForRender(this.state.accountNum, theAccount.accountNum, '')}
                                  onChange={(e) => this.setState({ accountNum: e.target.value })}
                                  helperText={this.getAccountNumberHelperText(theAccount.accountNum)}
                                  inputProps={{ maxLength: 64 }}
                                  InputProps={{ readOnly: customerTransferred || customerCancelled, disableUnderline: customerTransferred || customerCancelled }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                  Primary contact
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                  id="first-name" label="First name *"
                                  value={this.getValueForRender(this.state.firstName,
                                    theAccount.contactFirstName,
                                    '')}
                                  onChange={(e) => this.setState({ firstName: e.target.value })}
                                  inputProps={{ maxLength: 32 }}
                                  InputProps={{ readOnly: customerTransferred || customerCancelled, disableUnderline: customerTransferred || customerCancelled}}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                  id="last-name" label="Last name *"
                                  value={this.getValueForRender(this.state.lastName,
                                    theAccount.contactLastName,
                                    '')}
                                  onChange={(e) => this.setState({ lastName: e.target.value })}
                                  inputProps={{ maxLength: 32 }}
                                  InputProps={{ readOnly: customerTransferred || customerCancelled, disableUnderline: customerTransferred || customerCancelled}}
                                />
                              </Grid>
                              {/* className={classNames(classes.message, classes[this.props.status])} */}
                              {/* helperText={emailHelperText} */}
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                  id="email" label="Email *"
                                  error={emailError}
                                  value={this.getValueForRender(this.state.email,
                                    theAccount.contactEmail,
                                    '')}
                                  type='email'
                                  onChange={(e) => this.setState({ email: e.target.value })}
                                  inputProps={{ maxLength: 254 }}
                                  InputProps={{ readOnly: customerTransferred || customerCancelled, disableUnderline: customerTransferred  || customerCancelled}}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                {/* remove this grid item when we bring phone number back */}
                              </Grid>
                              {/*<Grid item xs={12} sm={6}>
                                <TextField  data-recording-ignore="mask"
                                  id="phone" label="Phone" 
                                  value={ this.getValueForRender(this.state.phone,
                                          theAccount.contactPhone,
                                          '') }
                                  onChange={ (e) => this.setState({ phone: e.target.value }) }
                                  InputProps={{
                                    inputComponent: PhoneNumberFormat,
                                  }}
                                />
                              </Grid>*/}
                              <React.Fragment>
                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                    Monthly statements
                                  </Typography>
                                </Grid>
                                {freshBooksIntegrationActive === true &&
                                  <Grid item xs={12}>
                                    <Paper className={classes.integrationPaper}>
                                      <Grid container spacing={2} direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Grid item>
                                          <Grid container spacing={1} direction="row" alignItems="center">
                                            <Grid item>
                                              <img alt="Logo" height="48px" src={freshBooksLogo} />
                                            </Grid>
                                            <Grid item>
                                              <Grid container spacing={0}>
                                                <Grid item>
                                                  <Typography align="left" variant="subtitle1">Invoice through FreshBooks</Typography>
                                                </Grid>
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="When you turn on FreshBooks invoicing for your customer, monthly statements in AppBind will be pushed into FreshBooks as invoices that bill your services and markups, and rebill expenses.">
                                                    <HelpOutline fontSize="small" style={{ verticalAlign: "-20%", marginLeft: '4px' }} />
                                                  </Tooltip>
                                                </Grid>
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        </Grid>

                                        {this.state.fbClients != null &&
                                          <Grid item>
                                            <FormControl variant="standard" className={classes.clientSelect}>
                                              {savedClientId == null &&
                                                <InputLabel htmlFor="client-select-label">Connect FreshBooks client</InputLabel>}
                                              {savedClientId != null &&
                                                <InputLabel htmlFor="client-select-label">Connected FreshBooks client</InputLabel>}
                                              <Select
                                                labelId="client-select-label"
                                                id="client-select"
                                                value={theFbClientId}
                                                label="Select FreshBooks client"
                                                onChange={(e) => this.setState({ fbClientId: e.target.value })}
                                              >
                                                {savedClientId == null && (theFbClientId != null && theFbClientId !== '') &&
                                                  <MenuItem key="UNSET" value="UNSET">{freshBooksNoConnectLabel}</MenuItem>}
                                                {fbClientsSorted.map(n => {
                                                  return (<MenuItem value={n.id} key={n.id}>{n.organization}</MenuItem>);
                                                })}
                                                {(savedClientId != null || (savedClientId == null && foundNameMatch === false)) &&
                                                <Divider />}
                                                {savedClientId == null && foundNameMatch === false &&
                                                <MenuItem key="NEW" value="NEW">{freshBooksAddLabel}</MenuItem>}
                                                {(savedClientId != null) &&
                                                <MenuItem key="DROP" value="DROP">Disconnect</MenuItem>}
                                              </Select>
                                            </FormControl>
                                          </Grid>}
                                      </Grid>
                                    </Paper>
                                  </Grid>}
                                <Grid item>
                                  <FormControlLabel
                                    control={
                                      <Switch checked={this.getValueForRender(this.state.sendMonthlyStatement, theAccount.sendMonthlyStatement, 0) === 1}
                                        disabled={commsEmailSwitchDisabled}
                                        onChange={(e, checked) => {
                                          if (checked) {
                                            this.setState({ sendMonthlyStatement: 1 });
                                          } else {
                                            this.setState({ sendMonthlyStatement: 0 });
                                          }
                                        }}
                                      />
                                    }
                                    label={'Email a monthly statement on the ' +  getDayLabel(theAccount.billingDayOfMonth) + ' of each month' }
                                  />
                                </Grid>
                                <Grid item style={{ marginLeft: -24, marginTop: 10 }}>
                                  <Tooltip placement='right' title='You can customize email that is sent using the communications menu item'>
                                    <HelpOutline fontSize="small" />
                                  </Tooltip>
                                </Grid>
                              </React.Fragment>

                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                  Mailing address
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Address
                                  showRequired={true}
                                  displayOnly={customerTransferred || customerCancelled}
                                  address={address}
                                  regionLabel={regionLabel}
                                  setCountryCodeState={this.setCountry}
                                  setRegionCodeState={this.setRegion}
                                  setCityState={this.setCity}
                                  setPostalCodeState={this.setPostalCode}
                                  setAddress1State={this.setAddress1}
                                  setAddress2State={this.setAddress2}
                                />
                              </Grid>
                              {/*
                              <Grid item xs={12}>
                                <TextField
                                  id="description" label="Description" 
                                  value={ this.getValueForRender(this.state.description,
                                          theAccount.description,
                                          '') }
                                  multiline
                                  onChange={ (e) => this.setState({ description: e.target.value }) }
                                />
                              </Grid>
                              */}

                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                                  Users
                                </Typography>
                              </Grid>

                              <Grid item xs={12}>
                                <TableContainer>
                                  <Table>
                                    <TableHead className={classes.tblHead}>
                                      <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        {/* If reseller, allow invite */
                                          isAuthenticatedReseller() &&
                                          <TableCell>
                                            {theAccount.accountStatus === 1 &&
                                              <Button
                                                className={classes.iconbutton}
                                                color="primary"
                                                variant="contained"
                                                disabled={true || isAccountSuspended()}
                                              >
                                                <Add className={classes.editicon} />&nbsp;Invite&nbsp;User
                                              </Button>
                                            }
                                            {theAccount.accountStatus === 0 &&
                                              <Tooltip title="Add and invite a customer user to Appbind">
                                                <Button
                                                  className={classes.iconbutton}
                                                  color="primary"
                                                  variant="contained"
                                                  disabled={customerTransferred  || customerCancelled || isAccountSuspended()}
                                                  onClick={() => { this.goToUserCommunications(false, newUserLink, theAccount); }}
                                                >
                                                  <Add className={classes.editicon} />&nbsp;Invite&nbsp;User
                                                </Button>
                                              </Tooltip>
                                            }
                                          </TableCell>
                                        }
                                        {/* If admin, show masquerade */
                                          isAuthenticatedAppBind() &&
                                          <React.Fragment>
                                            <TableCell />
                                            <TableCell />
                                          </React.Fragment>
                                        }
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {theAccount.userTsByAccountId.nodes.map(n => {
                                        let tlink = 'useredit?userId=' + n.userId + '&accountId=' + theAccount.accountId;
                                        return (
                                          <TableRow key={n.userId}>
                                            <TableCell data-recording-sensitive>{n.firstName} {n.lastName}</TableCell>
                                            <TableCell data-recording-sensitive>{n.email}</TableCell>
                                            {/*<TableCell data-recording-sensitive><PhoneNumberFormat readOnly={true} value={n.phone}/></TableCell>*/}
                                              <TableCell>
                                                { /* This shouldn't happen - current user shouldn't be in the account */}
                                                {uid !== n.userId &&
                                                  <Button
                                                    className={classes.iconbutton}
                                                    color="primary"
                                                    disabled={theAccount.accountStatus === 1 || customerTransferred  || customerCancelled}
                                                    onClick={this.goTo.bind(this, tlink)}
                                                  >
                                                    <Edit className={classes.editicon} />
                                                  </Button>
                                                }
                                              </TableCell>
                                            {/* If admin, show masquerade */
                                              isAuthenticatedAppBind() &&
                                              <TableCell>
                                                <Button
                                                  className={classes.mbutton}
                                                  variant="outlined"
                                                  onClick={this.masqueradeAs.bind(this, n.userId, theAccount.accountId, n.userArole, n.userName)}>
                                                  Masquerade
                                                </Button>
                                              </TableCell>
                                            }
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Grid>
                              <Grid item>
                                <SaveButton
                                  snackbarOpen={this.state.saveSnackbarOpen}
                                  autoHideDuration={this.state.saveSnackbarDuration}
                                  onSaveClick={() => { this._saveAction(acctId, theAccount, savedClientId); }}
                                  onSnackbarClose={() => { this._onSnackbarClose(); }}
                                  status={this.state.saveStatus}
                                  message={this.state.saveMessage}
                                  disabled={this.disableAccountSave(theAccount, savedClientId) || customerTransferred || customerCancelled} />
                              </Grid>
                              <Grid item xs={12}>
                              {/* Admin actions -- currently just "credit" and update mcc stuff */}
                              { isAuthenticatedAppBind() &&
                              <Grid container spacing={2} justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <Typography variant='h6'>Admin actions</Typography>
                                </Grid>

                                <Grid item>
                                  <CurrencyTextField
                                    id="acctcredit" label="Account credit"
                                    value={ this.state.acctCredit }
                                    onChange={ (e) =>  {
                                      //if the default note, is the same as start ('Account credit' or 'Account debit') and this value shifts, change.
                                      const valueChange = Number(e.target.value);

                                      if (valueChange > 0 && this.state.creditNotes === 'Account debit') {
                                        this.setState({acctCredit: e.target.value, creditNotes: 'Account credit'});
                                      } else if (valueChange < 0 && this.state.creditNotes === 'Account credit') {
                                        this.setState({acctCredit: e.target.value, creditNotes: 'Account debit'});
                                      } else {
                                        this.setState({ acctCredit: e.target.value });
                                      }
                                    }}
                                    showErrMsg={true}
                                    allowNegative={true}
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item>
                                  <TextField
                                    id="notes" label="Notes"
                                    value={ this.state.creditNotes }
                                    onChange={ (e) => this.setState({ creditNotes: e.target.value }) }
                                  />
                                </Grid>
                                <Grid item>
                                  <SaveButton
                                    color="secondary"
                                    variant="outlined"
                                    label="Issue credit/debit"
                                    snackbarOpen={this.state.saveSnackbarOpen}
                                    autoHideDuration={this.state.saveSnackbarDuration}
                                    onSaveClick={() => { this._initiateCreditOrDebit(acctId); }}
                                    onSnackbarClose={() => { this._onSnackbarClose(); }}
                                    status={this.state.saveStatus}
                                    message={this.state.saveMessage}
                                    disabled={this.disableCreditButton()}/>
                                </Grid>
                                { !this.state.showTopupSpinner &&
                                <Grid item>
                                  <Button color="primary" variant="outlined"  disabled={customerCancelled} className={classes.button} onClick={() => { this.checkAndProcessAutoTopup(acctId); }}>
                                    Check / Process Auto Topup
                                  </Button>
                                </Grid>}
                                { this.state.showTopupSpinner &&
                                <Grid item>
                                  <Button disabled={false} color="primary" variant="outlined" className={classes.button}>
                                    <SavingSpinner/>
                                  </Button>
                                </Grid>}

                                <Grid item xs={12}/>
                                  <Grid item xs={3}>
                                    <FormControl variant="standard"
                                      className={classes.formControl}
                                      style={{'width': '97%'}}
                                    >
                                      <InputLabel shrink htmlFor="accountStatus">Account status</InputLabel>
                                      <Select
                                        value={this.getValueForRender(this.state.accountStatus, theAccount.accountStatus, 0)}
                                        inputProps={{
                                          name: 'accountStatus',
                                          id: 'accountStatus',
                                        }}
                                        onChange={(e) => { this.setState({ accountStatus: e.target.value }); } }
                                        disabled={theAccount.accountStatus === 2}
                                        className={classes.nselect}
                                      >
                                        <MenuItem value={0}>Active</MenuItem>
                                        <MenuItem value={1}>Suspended</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <FormControl variant="standard"
                                      className={classes.formControl}
                                      style={{'width': '60%'}}
                                    >
                                      <InputLabel shrink htmlFor="billingDom">Billing day of month</InputLabel>
                                      <Select
                                        value={this.getValueForRender(this.state.billingDayOfMonth, theAccount.billingDayOfMonth, 31)}
                                        inputProps={{
                                          name: 'billingDom',
                                          id: 'billingDom',
                                        }}
                                        onChange={(e) => { this.setState({ billingDayOfMonth: e.target.value }); } }
                                        className={classes.nselect}
                                      >
                                        <MenuItem value={1}>1</MenuItem>
                                        <MenuItem value={2}>2</MenuItem>
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={4}>4</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={6}>6</MenuItem>
                                        <MenuItem value={7}>7</MenuItem>
                                        <MenuItem value={8}>8</MenuItem>
                                        <MenuItem value={9}>9</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={11}>11</MenuItem>
                                        <MenuItem value={12}>12</MenuItem>
                                        <MenuItem value={13}>13</MenuItem>
                                        <MenuItem value={14}>14</MenuItem>
                                        <MenuItem value={15}>15</MenuItem>
                                        <MenuItem value={16}>16</MenuItem>
                                        <MenuItem value={17}>17</MenuItem>
                                        <MenuItem value={18}>18</MenuItem>
                                        <MenuItem value={19}>19</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={21}>21</MenuItem>
                                        <MenuItem value={22}>22</MenuItem>
                                        <MenuItem value={23}>23</MenuItem>
                                        <MenuItem value={24}>24</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={26}>26</MenuItem>
                                        <MenuItem value={27}>27</MenuItem>
                                        <MenuItem value={28}>28</MenuItem>
                                        <MenuItem value={29}>29</MenuItem>
                                        <MenuItem value={30}>30</MenuItem>
                                        <MenuItem value={31}>31</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <CurrencyTextField 
                                      id="maxCharge" label="Max charge"
                                      value={ this.getValueForRender(this.state.maxCharge, 
                                              theAccount.maxCharge,
                                              '') }
                                      onChange={ (e) => this.setState({ maxCharge: e.target.value }) }
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={0}
                                      minEnterValue={0}
                                      textAlign="right"
                                    />
                                  </Grid>
                                  <Grid item xs={2}>
                                    <CurrencyTextField 
                                      id="float-limit" label="Float limit"
                                      value={ this.getValueForRender(this.state.floatLimit, 
                                              theAccount.floatLimit,
                                              '') }
                                      onChange={ (e) => this.setState({ floatLimit: e.target.value }) }
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={0}
                                      minEnterValue={0}
                                      textAlign="right"
                                    />
                                  </Grid>
                                  <Grid item xs={2}>
                                    <CurrencyTextField 
                                      id="req-float-limit" label="Requested Float limit"
                                      value={ this.getValueForRender(this.state.requestedFloatLimit, 
                                              theAccount.requestedFloatLimit,
                                              '') }
                                      onChange={ (e) => this.setState({ requestedFloatLimit: e.target.value }) }
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={0}
                                      minEnterValue={0}
                                      textAlign="right"
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <TextField
                                          id="MCCWhitelist" label="MCC Whitelist (comma separated, no spaces)"
                                          value={ this.getValueForRender(this.state.mccWhitelist, theAccount.mccWhitelist, '') }
                                          onChange={ (e) => this.setState({ mccWhitelist: e.target.value }) }
                                          inputProps={{ maxLength: 2048 }}
                                        />
                                      </Grid>
                                      
                                      <Grid item xs={3}>
                                        <FormControl variant="standard"
                                          className={classes.formControl}
                                          style={{'width': '97%'}}
                                        >
                                          <InputLabel shrink htmlFor="use_budgets">MCC Authorization</InputLabel>
                                          <Select
                                            value={this.getValueForRender(this.state.mccAuth, theAccount.mccAuth, 0)}
                                            inputProps={{
                                              name: 'mccAuth',
                                              id: 'mccAuth',
                                            }}
                                            onChange={(e) => { this.setState({ mccAuth: e.target.value }); } }
                                            className={classes.nselect}
                                          >
                                            <MenuItem value={0}>Whitelist only</MenuItem>
                                            <MenuItem value={1}>Allow blacklist</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Grid>

                                      {this.state.alsAssetBalance != null &&
                                      <Grid item xs={12}>
                                        <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-end">
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Typography align="center" variant="h6">Balance</Typography>
                                                </Grid>
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="Total equity - what we'll show as the balance for the account">
                                                    <Typography align="center" variant="body1">Equity balance</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsEquityBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Tooltip placement='bottom' title='Asset balance for the account - generally usable funds'>
                                                    <Typography align="center" variant="body1">Asset</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="Total liabilities - typically what is owed to AppBind, which should only occur when we provide a float.">
                                                    <Typography align="center" variant="body1">Liability</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsLiabilityBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="This is the total virtual card charge amount that we've authorized but hasn't cleared yet.  We don't bill until the auth clears, as it could be reversed.">
                                                    <Typography align="center" variant="body1">Auths</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAuthBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="These are inbound payments that haven't arrived yet (e.g. ACH top-up that hasn't cleared yet)">
                                                    <Typography align="center" variant="body1">Topup ttl</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInAr)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="This is the portion of inbound payments that haven't arrived yet that we've credited to the account for used in subscription purchases.">
                                                    <Typography align="center" variant="body1">Topup avl</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInArAvailable)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Typography align="center" variant="h6">Available</Typography>
                                                </Grid>
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="Previous amount less any outstanding auths.  This is the amount that is shown to a reseller as available balance.">
                                                    <Typography align="center" variant="body1">Available balance</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetAndArBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Typography align="center" variant="h6">Max Payout</Typography>
                                                </Grid>
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="The maximum amount we'll allow to be paid out.  This is asset less liabilities.">
                                                    <Typography align="center" variant="body1">Maximum payout</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsMaxPayoutBalance)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          <Grid item>
                                            <Paper className={classes.alsPaper}>
                                              <Grid container spacing={0} direction="column">
                                                <Grid item>
                                                  <Typography align="center" variant="h6">Float</Typography>
                                                </Grid>
                                                <Grid item>
                                                  <Tooltip placement='bottom' title="The amount we're currently floating to this account.">
                                                    <Typography align="center" variant="body1">Float balance</Typography>
                                                  </Tooltip>
                                                </Grid>
                                                <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsFloatBalanceDisplay)}</Typography></Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                          { isAdminUser() &&
                                          <Grid item>
                                            <Button 
                                              color="primary"
                                              variant="outlined"
                                              onClick={() => {this.goTo(viewLedgerLink);}}>
                                              View ledgers
                                            </Button>
                                          </Grid>}
                                          <Grid item>
                                            <Button 
                                              color="primary"
                                              variant="outlined"
                                              onClick={() => {this.goTo( 'featureflagmanage?accountId=' + theAccount.accountId);}}>
                                              Edit Feature Flags
                                            </Button>
                                          </Grid>
                                        </Grid>
                                      </Grid>}

                                      <Grid item xs={12}>
                                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                          {'Fraud protection'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <PercentageTextField 
                                          id="plaid-balance % allow"
                                          label="Plaid balance % allow"
                                          className={classes.percentField}
                                          value={this.getValueForRender(this.state.plaidCheckPercAllow, theAccount.plaidCheckPercAllow, '') }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          maxValue={100}
                                          minEnterValue={-9999}
                                          maxEnterValue={9999}
                                          onChange={ (e) => { this.setState({ plaidCheckPercAllow: e.target.value }); } }
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <CurrencyTextField 
                                          id="achLimitNoPeek" label="ACH limit no peek"
                                          value={ this.getValueForRender(this.state.achLimitNopeek, 
                                                  theAccount.achLimitNopeek,
                                                  '') }
                                          onChange={ (e) => this.setState({ achLimitNopeek: e.target.value }) }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          minEnterValue={0}
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <CurrencyTextField 
                                          id="achLimitAsolute" label="ACH limit absolute"
                                          value={ this.getValueForRender(this.state.achLimitAbsolute, 
                                                  theAccount.achLimitAbsolute,
                                                  '') }
                                          onChange={ (e) => this.setState({ achLimitAbsolute: e.target.value }) }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          minEnterValue={0}
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <PercentageTextField 
                                          id="ach-%-allow-charge"
                                          label="ACH % available on charge"
                                          className={classes.percentField}
                                          value={this.getValueForRender(this.state.achPercAvailableOnCharge, theAccount.achPercAvailableOnCharge, '') }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          maxValue={100}
                                          minEnterValue={-9999}
                                          maxEnterValue={9999}
                                          onChange={ (e) => { this.setState({ achPercAvailableOnCharge: e.target.value }); } }
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <CurrencyTextField 
                                          id="achMinAvailableOnCharge" label="ACH min available on charge"
                                          value={ this.getValueForRender(this.state.achMinAvailableOnCharge, 
                                                  theAccount.achMinAvailableOnCharge,
                                                  '') }
                                          onChange={ (e) => this.setState({ achMinAvailableOnCharge: e.target.value }) }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          maxValue={5000}
                                          minEnterValue={-9999}
                                          maxEnterValue={9999}
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <PercentageTextField 
                                          id="ccPercAvailableOnCharge"
                                          label="CC % available on charge"
                                          className={classes.percentField}
                                          value={this.getValueForRender(this.state.ccPercAvailableOnCharge, theAccount.ccPercAvailableOnCharge, '') }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          maxValue={100}
                                          minEnterValue={-9999}
                                          maxEnterValue={9999}
                                          onChange={ (e) => { this.setState({ ccPercAvailableOnCharge: e.target.value }); } }
                                          textAlign="right"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <CurrencyTextField 
                                          id="ccMinAvailableOnCharge" label="CC min available nn charge"
                                          value={ this.getValueForRender(this.state.ccMinAvailableOnCharge, 
                                                  theAccount.ccMinAvailableOnCharge,
                                                  '') }
                                          onChange={ (e) => this.setState({ ccMinAvailableOnCharge: e.target.value }) }
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={0}
                                          maxValue={5000}
                                          minEnterValue={-9999}
                                          maxEnterValue={9999}
                                          textAlign="right"
                                        />
                                      </Grid>    

                                      { !this.state.showAdminSavingSpinner &&
                                      <Grid item xs={3} style={{marginTop: 8}}> 
                                        <Button color="primary" 
                                                variant="contained"
                                                disabled={this.disableAdminAccountSave(theAccount)} 
                                                onClick={() => { this.adminSave(acctId);}}>
                                            Save
                                        </Button>
                                      </Grid>}
                                      { this.state.showAdminSavingSpinner &&
                                      <Grid item xs={3} style={{marginTop: 8}}> 
                                        <Button disabled={false} color="primary" variant="contained" >
                                          <SavingSpinner/>
                                        </Button>
                                      </Grid>}

                                      { !this.state.showReactivateSpinner && customerCancelled &&
                                      <Grid item>
                                        <Button color="primary" variant="outlined"  className={classes.button} onClick={() => { this._reactivateCustomer(acctId); }}>
                                          Reactivate Customer
                                        </Button>
                                      </Grid>}
                                      { this.state.showReactivateSpinner &&
                                      <Grid item>
                                        <Button disabled={false} color="primary" variant="outlined" className={classes.button}>
                                          <SavingSpinner/>
                                        </Button>
                                      </Grid>}
                                    </Grid>
                                  </Grid>
                                </Grid>}
                              </Grid>
                            </Grid>
                          </TabContainer>}

                          {value === 1 &&
                          <TabContainer {...this.props}>
                            <Grid container spacing={2} justifyContent="flex-start">
                              {showServices === true &&
                              <Grid item xs={12}>
                                <Typography variant="h6">
                                  Manage subscriptions and services for your customer
                                </Typography>
                                <Typography variant="body2">
                                  View subscriptions and services that you're managing for your customer.
                                </Typography>
                              </Grid>}
                              {showServices === false &&
                              <Grid item xs={12}>
                                <Typography variant="h6">
                                  Manage subscriptions for your customer
                                </Typography>
                                <Typography variant="body2">
                                  View subscriptions that you're managing for your customer.
                                </Typography>
                              </Grid>}

                              <Grid item xs={12}>
                                <Paper className={classes.paper}>                                 
                                  <Grid container spacing={1} justifyContent="space-between">
                                    <Grid item>
                                      <Typography variant="h6">
                                        Subscriptions
                                      </Typography>
                                    </Grid>                                    
                                  </Grid>

                                  <Subscriptions customerAccountId={theAccount.accountId}
                                    auth={this.props.auth} history={this.props.history}
                                    custAppLink={custAppLink}
                                    customerName={customerName}
                                    contactFirstName={theAccount.contactFirstName}
                                    contactLastName={theAccount.contactLastName}
                                    contactEmail={theAccount.contactEmail}
                                    customerUseBudgets={theAccount.useBudgets}
                                    account={theAccount}
                                    showInvite={anyUserInvite}
                                    setTabVal={this.setTabValue}
                                    accountStatus={theAccount.accountStatus}
                                    location={this.props.location} />
                                </Paper>
                              </Grid>

                              {showServices === true &&
                              <Grid item xs={12}>
                                <Paper className={classes.paper}>
                                  <Services customerAccountId={theAccount.accountId}
                                    auth={this.props.auth} history={this.props.history}
                                    custAppLink={custAppLink}
                                    customerName={customerName}
                                    contactFirstName={theAccount.contactFirstName}
                                    contactLastName={theAccount.contactLastName}
                                    contactEmail={theAccount.contactEmail}
                                    customerUseBudgets={theAccount.useBudgets}
                                    account={theAccount}
                                    showInvite={anyUserInvite}
                                    setTabVal={this.setTabValue}
                                    accountStatus={theAccount.accountStatus} />
                                </Paper>
                              </Grid>}

                              {showSOs === true &&
                              <Grid item xs={12}>
                                <Paper className={classes.paper}>
                                  <SubscriptionOrders accountId={theAccount.accountId}
                                    auth={this.props.auth} history={this.props.history}
                                    account={theAccount}
                                    accountType={props.accountType}
                                    location={this.props.location}/>
                                </Paper>
                              </Grid>}
                            </Grid>
                          </TabContainer>}

                          {/* disabled={Number(this.state.acctCredit) !== NaN && Number(this.state.acctCredit) === 0} */}
                          {value === 2 &&
                          <TabContainer {...this.props}>
                            <FundingSourceList
                              cp="customer"
                              accountId={theAccount.accountId}
                              uInfo={uInfo}
                              auth={this.props.auth}
                              anyUserInvite={anyUserInvite}
                              accountStatus={theAccount.accountStatus}
                              resellerStatus={resellerStatus}
                              history={this.props.history}
                            />
                          </TabContainer>}

                          {value === 3 &&
                          <TabContainer {...this.props}>

                            {showWallet === true && !hasWallet &&
                            <CustomerBudget
                              accountId={theAccount.accountId}
                              auth={this.props.auth}
                              history={this.props.history}
                              useBudgets={theAccount.useBudgets}
                              resellerStatus={resellerStatus}
                              invitationNeeded={invitationNeeded} />}

                            {showWallet === true && hasWallet &&
                            <CustomerProjectWallet
                              accountId={theAccount.accountId}
                              auth={this.props.auth}
                              history={this.props.history}
                              useBudgets={theAccount.useBudgets}
                              resellerStatus={resellerStatus}
                              invitationNeeded={invitationNeeded} />}

                            {showWallet === false &&
                            <CustomerChargeLimit
                              accountId={theAccount.accountId}
                              auth={this.props.auth}
                              history={this.props.history}
                              useBudgets={theAccount.useBudgets}
                              resellerStatus={resellerStatus} />}

                          </TabContainer>}

                          {value === 4 &&
                            <TabContainer {...this.props}>
                              <CustomerFinance
                                customerAccountId={theAccount.accountId}
                                projects={theAccount.projectTsByCustomerAccountId.nodes}
                                pAccountId={abEnvironment.getUserAttribute('a_id')}
                                fbClientId={theFbClientId}
                                fbLogo={freshBooksLogo}
                                auth={this.props.auth}
                                history={this.props.history}
                              />
                            </TabContainer>
                          }
                        </Grid>

                      </Grid>
                      <ConfirmationDialog
                        open={this.state.confirmSuspend}
                        onClose={() => { this.setState({ confirmSuspend: false }); }}
                        onConfirm={() => { this._suspendCustomer(acctId); }}
                        onCancel={() => { }}
                        title="Suspend customer"
                        cancelBtn="Cancel"
                        okBtn="Ok"
                      >
                        {suspendMessage}
                      </ConfirmationDialog>
                      <ConfirmationDialog
                        open={this.state.confirmDelete}
                        onClose={() => { this.setState({ confirmDelete: false }); }}
                        onConfirm={() => { this._deleteCustomer(acctId, users); }}
                        onCancel={() => { }}
                        title="Delete customer"
                        cancelBtn="Cancel"
                        okBtn="Ok"
                        irreversibleAction={true}
                      >
                        Deleting this customer is permanent. Are you sure you want to proceed?
                      </ConfirmationDialog>
                      <ConfirmationDialog
                        open={this.state.confirmReenable}
                        onClose={() => { this.setState({ confirmReenable: false }); }}
                        onConfirm={() => { this._reenableCustomer(acctId); }}
                        onCancel={() => { }}
                        title="Unsuspend customer"
                        cancelBtn="Cancel"
                        okBtn="Ok"
                      >
                        When a customer is unsuspended, subscription charges will be accepted, and the customer account will be fully active.  Are you sure you want to proceed?
                      </ConfirmationDialog>

                      {newTransfer && contactIsAUser &&
                        <ConfirmationDialog
                          open={this.state.confirmTransfer}
                          onClose={() => { this.setState({ confirmTransfer: false, anchorEl: null }); }}
                          onConfirm={() => { this.handleTransfer(acctId); }}
                          onCancel={() => { }}
                          title={"Transfer subscriptions to " + this.getValueForRender(this.state.accountName, theAccount.accountName, '') + "?"}
                          cancelBtn="Cancel"
                          okBtn="Yes, proceed"
                          irreversibleAction={true}
                        >

                          <Typography>
                            {transferConfirmationMsg}
                          </Typography>
                          <Typography style={{ marginTop: '16px' }}>
                            They will be asked to create their own AppBind account and add or update any payment methods if necessary.
                          </Typography>

                        </ConfirmationDialog>
                      }
                      {newTransfer && !contactIsAUser &&
                        <ConfirmationDialog
                          open={this.state.confirmTransfer}
                          onClose={() => { this.setState({ confirmTransfer: false, anchorEl: null }); }}
                          onConfirm={() => { }}
                          onCancel={() => { }}
                          title={"Transfer subscriptions to " + this.getValueForRender(this.state.accountName, theAccount.accountName, '')}
                          
                          okBtn="OK"
                          irreversibleAction={false}
                        >

                          <Typography>
                            {"When initiating a transfer, we send the transfer request to the email listed as the primary account contact (in this case " + theAccount.contactEmail + "). In order to accept a transfer, the owner of that email address must be a user of this account in AppBind. Please change the primary account contact email to an email for a user in the account."}
                          </Typography>


                        </ConfirmationDialog>
                      }
                      {newTransfer &&
                        <Dialog
                          disableEscapeKeyDown
                          open={this.state.transferConfirmed}
                          onClose={(event, reason) => { if (reason !== 'backdropClick') { this.setState({ transferConfirmed: false }); } }}
                          maxWidth="xs"
                        >
                          <DialogTitle id="confirmation-dialog-title">Confirmed. We are transferring subscriptions.</DialogTitle>
                          <DialogContent>
                            <DialogContentText>
                              <Typography>
                                Tell your customer to look for an email from AppBind with instructions. We will notify you once the process completes or it expires.
                              </Typography>
                              <Typography style={{ marginTop: '16px' }}>
                                NOTE. Any subscription charges will be expensed as normal until the transfer is finalized.
                              </Typography>
                            </DialogContentText>
                          </DialogContent>
                          <DialogActions>

                            <Button onClick={() => { this.setState({ transferConfirmed: false }); }} color="primary" variant="contained">
                              {'Ok'}
                            </Button>
                          </DialogActions>
                        </Dialog>
                      }

                      {!newTransfer &&
                        <ConfirmationDialog
                          open={this.state.confirmTransfer}
                          onClose={() => { this.setState({ confirmTransfer: false, anchorEl: null }); }}
                          onConfirm={() => { this.handleTransferOld(this.getValueForRender(this.state.accountName, theAccount.accountName, '')); }}
                          onCancel={() => { }}
                          title="Transfer subscriptions?"
                          cancelBtn="No"
                          okBtn="Yes, transfer subscriptions"
                        >
                          Your request to transfer control of subscriptions to your customer will be sent to AppBind support.  Do you wish to proceed?
                        </ConfirmationDialog>
                      }
                      <ConfirmationDialog
                        open={this.state.confirmDeleteSubs}
                        onClose={() => { this.setState({ confirmDeleteSubs: false, anchorEl: null }); }}
                        onConfirm={() => { }}
                        onCancel={() => { }}
                        title="Cancel subscriptions?"
                        cancelBtn="No"
                        okBtn="Yes, cancel subscriptions"
                      >
                        Cancelling subscriptions will cancel all virtual cards causing all future charges to be declined.  Do you wish to proceed?
                      </ConfirmationDialog>
                      <ConfirmationDialog
                        open={this.state.confirmUserUpgrade}
                        onClose={() => { this.setState({ confirmUserUpgrade: false }); }}
                        onConfirm={() => { this._upgradeCustomerUsers(acctId, users); }}
                        onCancel={() => { }}
                        title="Upgrade users"
                        cancelBtn="Cancel"
                        okBtn="Ok"
                        irreversibleAction={true}
                      >
                        Upgrading users are permanent.  Each user will have their own AppBind login. Are you sure you want to proceed?
                      </ConfirmationDialog>

                    </Paper>
                  </Grid>
                </Grid>
              );
            }
            return <LoadingSpinner />
          }}
        />
      );
    } else {
      return (
        <h4>
          You do not have access to this page.
        </h4>
      );
    }
  }

  _initiateCreditOrDebit(accountId) {
    //only allow this to be executed if we have admin role.
    const { isAuthenticatedAppBind, callPartnerApi } = this.props.auth;

    if (!isAuthenticatedAppBind()) {
      return;
    }

    this.setState({saveStatus: 'saving'});

    let aPjwt = abEnvironment.getPjwt();
    // asset type USD.
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';

    let payload = {
      operation: "initiateCredit",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        creditAmount: this.state.acctCredit,
        notes: this.state.creditNotes
      }
    };

    
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        let abpErrorInfo = response.err;
        if (abpErrorInfo != null && abpErrorInfo.message != null) {
          theErrorText = abpErrorInfo.message;
        }
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }
      
      let creditDebitText = Number(this.state.acctCredit) < 0 ? 'Debit' : 'Credit';
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: creditDebitText + ' applied', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  _onSnackbarClose() {
    this.setState({ saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  _deleteCustomer = (acctId, users) => {
    this.setState({ showSecondarySpinner: true });
    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();


    let payload = {
      operation: "deleteCustomer",
      params: {
        pJwt: aPjwt,
        resellerAccountId: abEnvironment.getUserAttribute('a_id'),
        customerAccountId: acctId,
        customerUsers: users
      }
    };

    //console.log(JSON.stringify(payload));

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(resp => {
        return resp.json();
      })
      .then(response => {

        let data = response.data

        let errors = data.errors;
        //console.log('error is = ' + error +  '. is it null? ' + (error !== null));
        //console.log('data is = ' + data +  '. is it null? ' + (data === null));
        if (errors && errors.length !== 0) {
          let error = errors[0].message;
          this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: error, saveSnackbarDuration: null });
        } else {
          //console.log(JSON.stringify(data));
          if (data === null) {
            this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'There was a problem trying to delete the customer.', saveSnackbarDuration: null });
          }
          this.goTo('customers');
        }

      })
      .catch(error => {
        //console.log('catch error: ' + error);
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'There was a problem trying to delete the customer.', saveSnackbarDuration: null });
      });
  }

  _suspendCustomer = (acctId) => {
    this.setState({ showSecondarySpinner: true });

    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "updateCustomerStatus",
      params: {
        pJwt: aPjwt,
        accountId: acctId,
        status: 1
      }
    };

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.status === 0 && response.accountT != null) {
          // Set local store with result
          let rAccountT = response.accountT;

          commitLocalUpdate(abEnvironment.getEnvironment(), store => {
            let theIdString = 'accountTByAccountId(accountId:"' + acctId + '")';
            let sacct = store.getRoot().getLinkedRecord(theIdString);
            if (sacct != null) {
              if (rAccountT.accountStatus != null) { sacct.setValue(rAccountT.accountStatus, 'accountStatus'); }
            }
          });

          this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Customer suspended.', saveSnackbarDuration: 3000 });
        } else {
          this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Suspend customer failed.', saveSnackbarDuration: null });
        }
      })
      .catch(error => {
        //console.log('catch error: ' + error);
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Suspend customer failed.', saveSnackbarDuration: null });
      });
  }

  _reenableCustomer = (acctId) => {
    this.setState({ showSecondarySpinner: true });

    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "updateCustomerStatus",
      params: {
        pJwt: aPjwt,
        accountId: acctId,
        status: 0
      }
    };

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.status === 0 && response.accountT != null) {
          // Set local store with result
          let rAccountT = response.accountT;

          commitLocalUpdate(abEnvironment.getEnvironment(), store => {
            let theIdString = 'accountTByAccountId(accountId:"' + acctId + '")';
            let sacct = store.getRoot().getLinkedRecord(theIdString);
            if (sacct != null) {
              if (rAccountT.accountStatus != null) { sacct.setValue(rAccountT.accountStatus, 'accountStatus'); }
            }
          });

          this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Customer re-enabled.', saveSnackbarDuration: 3000 });
        } else {
          this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Suspend customer failed.', saveSnackbarDuration: null });
        }
      })
      .catch(error => {
        //console.log('catch error: ' + error);
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Reenable customer failed.', saveSnackbarDuration: null });
      });
  }

  checkAndProcessAutoTopup(acctId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({ showTopupSpinner: true });

    let payload = {
      operation: "checkAndProcessAutoTopup",
      params: {
        accountId: acctId
      }
    }

    callPartnerApi('appbind/generic',
                     'POST',
                     payload
    )
    .then((response) => {
      return response.json();
    })
    .then(() => {
      this.setState({ showTopupSpinner: false });      
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showTopupSpinner: false });
    });

  }

  /**
   * Call update on customer account and/or create/remove and integrated account link (for now FreshBooks)
   */
  _saveAction(acctId, accountProps, savedFbClientId) {
    this.setState({ saveStatus: 'saving' });

    const { accountName, accountNum, description,
      lastName, firstName, email, phone,
      address1, address2, city, state, country, postalCode, sendMonthlyStatement,
      fbClientId } = this.state;
    const { callPartnerApi } = this.props.auth;

    // What needs to be saved?
    let saveAccount = false;
    if (!this.disableAccountAttributeSave(accountProps)) {
      saveAccount = true;
    }
    let mapFbClient = false;
    if (this.isFbClientIdChanged(savedFbClientId) && fbClientId != null) {
      mapFbClient = true;
    }

    if (!saveAccount && !mapFbClient) {
      // This should not happen
      this.setState({ saveStatus: '', saveMessage: '' });
      return;
    }

    Promise.resolve()
      .then(() => {
        if (saveAccount) {
          return AccountUpdateMutation.commit(acctId, accountName, accountNum, description, null,
            lastName, firstName, email, phone,
            address1, address2, city, state, country, postalCode,
            null, null, null, null, sendMonthlyStatement, null, null, null);
        } else {
          return null;
        }
      })
      .then(response => {
        if (mapFbClient) {
          // Call API for FreshBooks link
          let payload = null;
          if (fbClientId === 'NEW') {
            payload = {
              operation: "FreshBooksLinkAccountToClient",
              params: {
                abAccountIdToLink: acctId,
                fbClientId: null,
                mode: 'Link'
              }
            };
          } else if (fbClientId === 'DROP') {
            payload = {
              operation: "FreshBooksLinkAccountToClient",
              params: {
                abAccountIdToLink: acctId,
                fbClientId: savedFbClientId,
                mode: 'Unlink'
              }
            };
          } else {
            payload = {
              operation: "FreshBooksLinkAccountToClient",
              params: {
                abAccountIdToLink: acctId,
                fbClientId: fbClientId,
                mode: 'Link'
              }
            };
          }

          return callPartnerApi('appbind/generic',
            'POST',
            payload);
          //window.location.reload();
        } else {
          return null;
        }
      })
      .then(response => {
        let saveMessage = 'Customer saved';
        /*
        if (saveAccount && mapFbClient) {
          saveMessage = 'Customer saved and FreshBooks link updated';
        } else if (saveAccount) {
          saveMessage = 'Customer saved';
        } else {
          saveMessage = 'FreshBooks link updated';
        }
        */
        if (mapFbClient) {
          window.location.reload();
        } else {
          this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: saveMessage, saveSnackbarDuration: 3000 });
        }
      })
      .catch(error => {
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Unable to update customer', saveSnackbarDuration: null });
      });
  }

  _updateCustomer(acctId) {
    this.setState({ saveStatus: 'saving' });
    const { accountName, accountNum, description,
      lastName, firstName, email, phone,
      address1, address2, city, state, country, postalCode, sendMonthlyStatement } = this.state;


    AccountUpdateMutation.commit(acctId, accountName, accountNum, description, null,
      lastName, firstName, email, phone,
      address1, address2, city, state, country, postalCode,
      null, null, null, null, sendMonthlyStatement, null, null, null)
      .then(response => {
        // console.log('Account updated')
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Customer saved.', saveSnackbarDuration: 3000 });
      })
      .catch(error => {
        // console.log('Got error: ' + error);
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Save customer failed.', saveSnackbarDuration: null });
      });
  }

  handleTransfer(accountId) {
    const { callPartnerApi } = this.props.auth;

    let forceFetch = this.state.forceFetch;
    forceFetch++;

    let pAccountId = abEnvironment.getUserAttribute('a_id');
    let aPjwt = abEnvironment.getPjwt();


    let payload = {
      operation: "initiateCustomerTransfer",
      params: {
        pJwt: aPjwt,
        resellerAccountId: pAccountId,
        customerAccountId: accountId
      }
    }

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(response => {
        this.setState({ transferConfirmed: true, anchorEl: null, forceFetch: forceFetch, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Customer transfer initiated.', saveSnackbarDuration: 3000 });
      })
      .catch(error => {
        this.setState({ anchorEl: null, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'There was a problem trying to initiate transfer.', saveSnackbarDuration: null });
      });

  }

  handleTransferOld(customerName) {
    const { callPartnerApi } = this.props.auth;

    let pAccountId = abEnvironment.getUserAttribute('a_id');
    let aPjwt = abEnvironment.getPjwt();
    let theBody = 'You requested that all subscriptions for ' + customerName + ' be transferred to their control.'

    // alert('Do a transfer from ' + abEnvironment.getUserAttribute('a_accountName') + ' to ' + customerName);

    let payload = {
      operation: "createNotification",
      params: {
        pJwt: aPjwt,
        accountId: pAccountId,
        notificationType: 'transfer',
        severity: 'info',
        subject: 'Transfer subscription request',
        body: theBody
      }
    }

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(response => {
        this.setState({ anchorEl: null });
      })
      .catch(error => {
        this.setState({ anchorEl: null });
      });

  }

  _reactivateCustomer(acctId) {

    const { logError } = this.props.auth;
    this.setState({ showReactivateSpinner: true });
    
    AccountUpdateMutation.commit(acctId, null, null, null, 0, 
                                 null, null, null, null, 
                                 null, null, null, null, null, null,
                                 null, null, null, null, null, null, null, null, null, null)
    .then(response => {
      this.setState({ showReactivateSpinner: false });      
      window.location.reload();
    })
    .catch(error => {
      logError('Exception in reseller.js: ' + error);
      this.setState({ showReactivateSpinner: false });      
      window.location.reload();
    });

  }

  _upgradeCustomerUsers = (acctId, users) => {
    const { callPartnerApi } = this.props.auth;

    //get users that are unauth into an array to post
    let usersToUpgrade = [];
    for (let i = 0; i < users.length; i++) {
      let user = users[i];
      if (user.userArole === 'appbind_customer_unauth') {
        usersToUpgrade.push(user);
      }
    }

    if (usersToUpgrade.length === 0) {
      //this should not happen as the menu item should be dim if noone to upgrade.
      return;
    }

    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "upgradeUsersToCustomer",
      params: {
        pJwt: aPjwt,
        pAccountId: abEnvironment.getUserAttribute('a_id'),
        userAccountId: acctId,
        users: usersToUpgrade
      }
    };

    callPartnerApi('appbind/generic',
      'POST',
      payload
    )
      .then(resp => {
        return resp.json();
      })
      .then(response => {

        this.setState({ anchorEl: null, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Users have been upgraded.', saveSnackbarDuration: 3000 });

      })
      .catch(error => {
        //console.log('catch error: ' + error);
        this.setState({ anchorEl: null, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'There was a problem trying to upgrade the users.', saveSnackbarDuration: null });
      });
  }

  adminSave = (acctId) => {
    this.setState({showAdminSavingSpinner: true});
 
    const { accountStatus, billingDayOfMonth, maxCharge, floatLimit, mccAuth, mccWhitelist, requestedFloatLimit,
      plaidCheckPercAllow, achLimitNopeek, achLimitAbsolute,  achPercAvailableOnCharge, achMinAvailableOnCharge, ccPercAvailableOnCharge, ccMinAvailableOnCharge} = this.state;

    AccountUpdateMutation.commit(acctId, null, null, null, accountStatus, 
                                null, null, null, null,   null, null, null, null, null, null,
                                null, billingDayOfMonth, maxCharge, floatLimit, null, null, null, null, mccAuth, mccWhitelist, null, null, null, requestedFloatLimit,
                                plaidCheckPercAllow, achLimitNopeek, achLimitAbsolute, achPercAvailableOnCharge, achMinAvailableOnCharge, 
                                ccPercAvailableOnCharge, ccMinAvailableOnCharge)
    .then(response => {
        // console.log('Account updated')
        this.setState({ showAdminSavingSpinner: false});
    }).catch(error => {
      console.log('Got error (admin eyes only): ' + error);
      this.setState({ showAdminSavingSpinner: false });
    });
    
  }

}

Customer.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(Customer);
